import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../contexts/use-auth';
import type { AppRole } from '../contexts/auth-context';
import type { Permission } from '../lib/permissions';

interface Props {
  children: ReactNode;
  requiredRole?: AppRole;
  requiredPermission?: Permission;
}

export function ProtectedRoute({ children, requiredRole, requiredPermission }: Props) {
  const { status, role, can } = useAuth();

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (status === 'signed-out' || status === 'unauthorized') {
    return <Navigate to="/login" replace />;
  }
  if (requiredRole && role !== requiredRole) {
    return <AccessDenied label={`role ${requiredRole}`} />;
  }
  if (requiredPermission && !can(requiredPermission)) {
    return <AccessDenied label={`permission ${requiredPermission}`} />;
  }
  return <>{children}</>;
}

function AccessDenied({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
      <Lock className="h-10 w-10 text-muted-foreground mb-3" />
      <h2 className="text-lg font-semibold">Access restricted</h2>
      <p className="text-sm text-muted-foreground mt-1">
        This page requires <span className="font-medium text-foreground">{label}</span>.
      </p>
    </div>
  );
}
